// EventVenueUploadPage.jsx (React 19 + MUI, Toss-style UI)
// ✅ 기존 로직(업로드/로딩/문구/예시표) 그대로 유지하면서 Tailwind -> MUI로 전환
// - 업로드 시 로딩 Backdrop + CircularProgress
// - 안내 문구는 Alert로 강조
// - 예시 표는 MUI Table로 표현(스크롤 유지)
// - 파일 선택은 Button + 숨김 input(토스 느낌)

import React, { useMemo, useRef, useState } from "react";

import {
    Alert,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { notify } from "../../../util/toast";

import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import LoadingDots from "../../../component/common/LoadingDots";
import ActionButton from "../../../component/common/ActionButton";
import ResizableTable from "../../../component/common/ResizableTable";
import { ds } from "../../../styles/designSystem";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { eventVenueApi } from "./api/eventVenueApi";

export default function EventVenueUploadPage() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);


    const inputRef = useRef(null);

    const handleFileChange = (event) => {
        if (loading) return;
        const f = event.target.files?.[0] ?? null;
        setSelectedFile(f);
        if (f) notify(`파일 선택됨: ${f.name}`, "success");
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            const msg = "업로드할 파일을 선택해주세요.";
            notify(msg, "warning");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        await runApiAction({
            request: () => eventVenueApi.upload(formData),
            errorMsg: serverMsgOr("업로드에 실패했습니다. 파일 형식을 확인해주세요."),
            successMsg: (data) => typeof data === "string" ? data : data?.message || "업로드가 완료되었습니다.",
            setLoading,
            onError: (error) => console.error(error),
        });
    };

    // 예시 데이터(원본 그대로)
    const exampleRows = useMemo(
        () => [
            {
                id: "6",
                name: "스탠포드 리조트",
                name2: "스탠포드 호텔 앤 리조트 통영",
                addr: "경상남도 통영시 도남로 347(도남동)",
                region: "16경남",
                sigungu: "통영시",
                space: "세미나룸 1,2",
                min: "40",
                max: "60",
                note: "세미나 룸 1,2 통합가능",
            },
            {
                id: "7",
                name: "스탠포드 리조트",
                name2: "스탠포드 호텔 앤 리조트 통영",
                addr: "경상남도 통영시 도남로 347(도남동)",
                region: "16경남",
                sigungu: "통영시",
                space: "아틀라스",
                min: "60",
                max: "100",
                note: "",
            },
            {
                id: "8",
                name: "풍기관광호텔",
                name2: "풍기 관광호텔 영주",
                addr: "경상북도 영주시 풍기읍 풍기로 53",
                region: "15경북",
                sigungu: "영주시",
                space: "스카이뷰",
                min: "200",
                max: "200",
                note: "",
            },
            {
                id: "9",
                name: "더리버사이드호텔",
                name2: "더 리버사이드 호텔 서울",
                addr: "서울시 서초구 강남대로107길 6(잠원동)",
                region: "01서울",
                sigungu: "-",
                space: "콘서트홀",
                min: "220",
                max: "810",
                note: "",
            },
        ],
        []
    );

    return (
        <Box sx={ds.components.external.page}>
            <Container maxWidth="xl" sx={{ py: 3 }}>

                <Box>
                    <Typography sx={ds.typography.pageTitle}>
                        행사장 목록 엑셀 업로드
                    </Typography>
                    <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.5 }}>
                        엑셀(.xlsx) 업로드로 행사장 데이터를 일괄 등록합니다.
                    </Typography>
                </Box>

                <br/>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 2.5,
                        ...ds.components.external.card,
                    }}
                >
                    <Stack spacing={2.25}>
                        {/* 헤더 */}


                        {/* 경고/유의사항 */}
                        <Alert
                            severity="warning"
                            variant="outlined"
                            sx={{
                                borderRadius: 3,
                                borderColor: "rgba(255, 167, 38, 0.45)",
                                bgcolor: "rgba(255, 167, 38, 0.08)",
                                "& .MuiAlert-message": { fontWeight: 600 },
                            }}
                        >
                            <Box sx={{ fontWeight: 700, color: "rgba(160, 80, 0, 1)" }}>
                                업로드 시 기존의 모든 행사장 데이터가 초기화됩니다.
                            </Box>
                            <Box sx={{ mt: 1, fontSize: 13, color: "text.secondary", fontWeight: 800 }}>
                                <div>1) 엑셀 <b>첫 번째 시트</b>만 사용</div>
                                <div>2) <b>3번째 행부터</b> 실제 데이터가 존재해야 함 (1,2행은 헤더/설명)</div>
                                <div>3) 열 순서/개수는 예시 표와 <b>동일</b>해야 합니다.</div>
                            </Box>
                        </Alert>

                        <Divider />

                        {/* 예시 표 */}
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.2px" }}>
                                    [예시 데이터 표]
                                </Typography>
                                <Chip
                                    label="스크롤 가능"
                                    size="small"
                                    sx={{
                                        ...ds.components.external.chip,
                                    }}
                                />
                            </Stack>

                            <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{
                                    borderRadius: 1.5,
                                    borderColor: ds.colors.border,
                                    overflowX: "auto",
                                }}
                            >
                                <ResizableTable size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={thSx}>ID</TableCell>
                                            <TableCell sx={thSx}>사업장명</TableCell>
                                            <TableCell sx={thSx}>사업장명(변환)</TableCell>
                                            <TableCell sx={thSx}>상세 주소</TableCell>
                                            <TableCell sx={thSx}>지역구분</TableCell>
                                            <TableCell sx={thSx}>세부공간명</TableCell>
                                            <TableCell sx={thSx}>최소(명)</TableCell>
                                            <TableCell sx={thSx}>최대(명)</TableCell>
                                            <TableCell sx={thSx}>비고</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {exampleRows.map((r) => (
                                            <TableRow key={r.id} hover>
                                                <TableCell sx={tdSx}>{r.id}</TableCell>
                                                <TableCell sx={tdSx}>{r.name}</TableCell>
                                                <TableCell sx={tdSx}>{r.name2}</TableCell>
                                                <TableCell sx={tdSx}>{r.addr}</TableCell>
                                                <TableCell sx={tdSx}>{r.region}</TableCell>
                                                <TableCell sx={tdSx}>{r.space}</TableCell>
                                                <TableCell sx={tdSx}>{r.min}</TableCell>
                                                <TableCell sx={tdSx}>{r.max}</TableCell>
                                                <TableCell sx={tdSx}>{r.note}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </ResizableTable>
                            </TableContainer>
                        </Box>

                        <Divider />

                        {/* 파일 선택 + 업로드 */}
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".xlsx"
                                onChange={handleFileChange}
                                disabled={loading}
                                style={{ display: "none" }}
                            />

                            <ActionButton
                                variant="secondary"
                                startIcon={<UploadFileRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => inputRef.current?.click()}
                                disabled={loading}
                                label="파일 선택"
                                sx={{ px: 2.25, py: 1 }}
                            />

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
                                    {selectedFile ? selectedFile.name : "선택된 파일 없음"}
                                </Typography>
                                <Typography sx={{ mt: 0.25, fontSize: 12, fontWeight: 800, color: "text.secondary" }} noWrap>
                                    {selectedFile ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : "xlsx 파일을 선택해주세요."}
                                </Typography>
                            </Box>

                            <ActionButton
                                variant="upload"
                                onClick={handleUpload}
                                loading={loading}
                                disabled={!selectedFile}
                                label={loading ? "업로드 중" : "업로드"}
                                sx={{ px: 2.75, py: 1 }}
                            />
                        </Stack>

                    </Stack>
                </Paper>
            </Container>

            {/* 로딩 오버레이 */}
            <Backdrop
                open={loading}
                sx={{ zIndex: (t) => t.zIndex.modal + 10, bgcolor: "rgba(255,255,255,0.85)" }}
            >
                <LoadingDots label="업로드하고 있어요 (5분 이상 소요됩니다)" />
            </Backdrop>

        </Box>
    );
}

const thSx = {
    fontWeight: 800,
    fontSize: 12,
    color: ds.colors.textSecondary,
    bgcolor: ds.colors.bg,
    borderBottom: `1px solid ${ds.colors.border}`,
    whiteSpace: "nowrap",
};

const tdSx = {
    fontWeight: 800,
    fontSize: 12,
    color: ds.colors.textPrimary,
    borderBottom: `1px solid ${ds.colors.borderLight}`,
    whiteSpace: "nowrap",
};
